'use strict';

const Marionette = require('backbone.marionette');
const Backbone = require('backbone');
const _ = require('lodash');

const utils = require('./utils/params.js');

const namedParam = /(\(\?)?:\w+/g;
const optionalParam = /\((.*?)\)/g;

const Router = module.exports = Backbone.Router.extend({

    /*
     * Override Backbone.Router parameter parsing to extract path params
     */
    _extractParameters: function (route, fragment) {
        const me = this;

        me.currentRoute = route;

        const params = route.exec(fragment).slice(1);
        const mapping = me.routeMapping[route];

        const pathParams = {};

        if (mapping) {
            _.each(params.slice(0, -1), function (param, i) {
                pathParams[mapping[i]] = param;
            });
        }

        let queryParams = null;
        const candidateQueryParams = params[params.length - 1];
        if (candidateQueryParams) {
            queryParams = utils.queryStringToParams(candidateQueryParams);
        }

        return [pathParams, queryParams];
    },

    mount: function (id, path, Curation) {
        const me = this;
        const paths = [];

        if (!id) {
            console.warn('cannot mount a curation with an undefined id!');
            return paths;
        }

        if (!path) {
            console.warn('cannot mount a curation without a path!');
            return paths;
        }

        // NOTE should match: :id, :id/, :id(, :id)
        if (path.match(/:id($|\/|\(|\))/)) {
            console.warn('cannot mount a curation with path parameter `:id`!');
            return paths;
        }

        if (me.curationMapping[id]) {
            console.warn(`a curation with id ${id} has already been mounted.`);
        }

        const controller = me.controller;
        me.curationMapping[id] = path;

        function transitionCuration(curation) {
            if (me.loadedCuration) {
                controller.teardownCuration(me.loadedCuration);
            }

            me.loadedCuration = curation;
        }

        paths.push(path);
        me.route(path, path, function (pathParams, queryParams) {
            const params = _.extend({}, pathParams, queryParams);
            const curation = new Curation(me, {
                curation_id: id
            }, {
                queryParams: params
            });
            curation.path = path;

            transitionCuration(curation);
            controller.handler(curation);
        });

        const detailsPath = `${path}/details/:instanceId`;
        paths.push(detailsPath);
        me.route(detailsPath, `${id}Details`, function (pathParams, queryParams) {
            const params = _.extend({}, pathParams, queryParams);
            const curation = new Curation(me, {
                curation_id: id
            }, {
                queryParams: params
            });
            curation.path = path;

            transitionCuration(curation);
            controller.handler(curation);
        });

        const widgetPath = `${path}/widget/:widgetId`;
        paths.push(widgetPath);
        me.route(widgetPath, `${id}Details`, function (pathParams, queryParams) {
            const params = _.extend({}, pathParams, queryParams);
            const curation = new Curation(me, {
                curation_id: id
            }, {
                queryParams: params
            });
            curation.path = path;

            transitionCuration(curation);
            controller.handler(curation);
        });

        return paths;
    },

    route: function (path) {
        const me = this;
        const route = me._routeToRegExp(path);

        me.routeMapping[route] = _.map(path.match(namedParam), function (str) {
            return str.substr(1);
        });

        Marionette.AppRouter.prototype.route.apply(me, arguments);
    },

    /*
     * Silently navigate to the provided path
     */
    setPath: function (path, options) {
        Backbone.history.navigate(path, options);
    },

    getNavigationClickHandler: function () {
        const me = this;
        const isMac = window.navigator.platform.indexOf('Mac') > -1;
        const isWindows = window.navigator.platform.indexOf('Win') > -1;
        const handler = _.partial(Router.navigationClickHandler, window, me);

        return function router_onClick(e) {
            const isBlank = Backbone.$(e.currentTarget).attr('target') === '_blank';
            const isMeta = (isMac && e.metaKey) || // macOS/OSX
                (isWindows && e.shiftKey) || // Windows
                (e.button && e.button === 1); // middle mouse button

            if (!isMeta && !isBlank) {
                handler.apply(this, arguments);
            }
        };
    },

    // Intercept dashboard links to navigate via js instead of the
    // browser
    interceptNavigation: function () {
        const me = this;
        Backbone.$('body').on('click', 'a[href], button[href]', me.getNavigationClickHandler());
    },


    resolveCurationRoute: function (curationId, params) {
        const me = this;
        // NOTE backbone routes do not support leading slashes but we want to
        // return paths with them
        return `/${Router.resolveRoute(me.curationMapping[curationId], params)}`;
    },

    resolveCurationRouteObject: function (curationId, params) {
        const me = this;
        // NOTE backbone routes do not support leading slashes but we want to
        // return paths with them
        const url = Router.resolveUrlObject(me.curationMapping[curationId], params);
        url.pathname = `/${url.pathname}`;

        return url;
    },

    initialize: function (options) {
        const me = this;
        Backbone.Router.prototype.initialize.apply(me, arguments);
        me.controller = options.controller;
        me.routeMapping = {};
        me.curationMapping = {};

        // 404 handler needs to be the first route defined.
        Backbone.Router.prototype.route.call(me, '*notFound', 'notFound', function () {
            me.controller.handleUnknownRoute();
        });
        _.last(Backbone.history.handlers).notExplicitlyMatchable = true;
    }
});

/**
 * A handler that intercepts clicks and conditionally navigates.
 * @param {Object} window A reference to the window.
 * @param {Object} e The event object.
 */
Router.navigationClickHandler = function (window, router, e) {
    let href = Backbone.$(this).attr('href');

    // remove leading and trailing slashes
    href = href.replace(/^\/+|\/+$/g, '');
    const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, '');

    // slice out export routes
    const handledClick = _.some(Backbone.history.handlers, function (handler) {
        const route = handler.route;
        if (!handler.notExplicitlyMatchable && route.test(href)) {
            e.preventDefault();

            if (href !== currentPath + window.location.search) {
                // Defer navigation to unlock ui
                window.requestAnimationFrame(function () {
                    router.setPath(href, {
                        trigger: true
                    });
                });
            }

            return true;
        }
    });

    return handledClick;
};

Router.getRoutePattern = function (route, props) {
    if (props.instanceId) {
        route = `${route}/details/:instanceId`;
    } else if (props.widgetId) {
        route = `${route}/widget/:widgetId`;
    }

    return route;
};

function buildUrlObject(path, props) {
    function replaceVariable(name) {
        const includeSlash = (name[1] === '/');

        const match = name.match(/[^\(\/?:]+[^\)]/);
        if (!match) {
            return '';
        }

        const prop = _.head(match);
        let value = props[prop];

        if (value && includeSlash) {
            value = `/${value}`;
        }

        props = _.omit(props, prop);

        return value || '';
    }

    let resolvedPath = path.replace(optionalParam, replaceVariable);
    resolvedPath = resolvedPath.replace(namedParam, replaceVariable);

    const query = _.pickBy(props, function (value) {
        return !_.isUndefined(value) && !_.isNull(value);
    });

    return {
        pathname: resolvedPath,
        query: query
    };
}

Router.resolveUrlObject = function (route, props) {
    props = props || {};

    const path = Router.getRoutePattern(route, props);
    return buildUrlObject(path, props);
};

Router.resolveRoute = function (route, props) {
    props = props || {};

    const path = Router.getRoutePattern(route, props);
    const url = buildUrlObject(path, props);

    if (!_.size(url.query)) {
        return url.pathname;
    }

    return `${url.pathname}?${utils.paramsToQueryString(url.query)}`;
};
